
import { supabase } from "@/integrations/supabase/client";
import { AnalyticsEvent, AnalyticsEventType } from "./types";

// Record a general analytics event
export const recordAnalyticsEvent = async (
  eventType: AnalyticsEventType, 
  eventData: Record<string, any> = {}
): Promise<boolean> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    // Prepare the analytics event
    const event: AnalyticsEvent = {
      event_type: eventType,
      event_data: eventData,
      user_id: userId,
      page_url: window.location.pathname,
      timestamp: new Date().toISOString()
    };
    
    // For anonymous users, store locally first
    if (!userId) {
      storeLocalAnalyticsEvent(event);
      return true;
    }
    
    // For authenticated users, send to Supabase
    // Use type assertion to bypass TypeScript issues with tables not yet in the types
    const { error } = await supabase
      .from('analytics_events' as any)
      .insert([event as any]);
    
    if (error) {
      console.error('Error recording analytics event:', error);
      storeLocalAnalyticsEvent(event); // Fallback to local storage
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in recordAnalyticsEvent:', error);
    return false;
  }
};

// Store events locally for offline/anonymous usage
export const storeLocalAnalyticsEvent = (event: AnalyticsEvent): void => {
  try {
    const existingEvents = localStorage.getItem('craveSOS_analyticsEvents');
    const events = existingEvents ? JSON.parse(existingEvents) : [];
    events.push(event);
    
    // Limit the number of stored events to prevent excessive local storage use
    const limitedEvents = events.slice(-50); // Keep last 50 events
    localStorage.setItem('craveSOS_analyticsEvents', JSON.stringify(limitedEvents));
  } catch (e) {
    console.error("Error storing analytics event locally:", e);
  }
};

// Sync locally stored events when user logs in
export const syncStoredAnalyticsEvents = async (): Promise<void> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    // Only proceed if user is authenticated
    if (!userId) return;
    
    const existingEvents = localStorage.getItem('craveSOS_analyticsEvents');
    if (!existingEvents) return;
    
    const events: AnalyticsEvent[] = JSON.parse(existingEvents);
    if (events.length === 0) return;
    
    // Update events with user ID
    const updatedEvents = events.map(event => ({
      ...event,
      user_id: userId
    }));
    
    // Batch insert to analytics_events table using type assertion to bypass TypeScript issues
    const { error } = await supabase
      .from('analytics_events' as any)
      .insert(updatedEvents as any[]);
    
    if (error) {
      console.error('Error syncing stored analytics events:', error);
      return;
    }
    
    // Clear local storage after successful sync
    localStorage.removeItem('craveSOS_analyticsEvents');
  } catch (error) {
    console.error('Error in syncStoredAnalyticsEvents:', error);
  }
};

// Track page views
export const trackPageView = (pagePath: string): void => {
  recordAnalyticsEvent('page_view', { path: pagePath });
};
